import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Megaphone } from "lucide-react";

// Two-line SVG sparkline (clicks vs submitted) over a fixed day window; flat zero line until data arrives.
function TrendChart({ series, days = 14 }) {
  const today = new Date();
  const dayKeys = Array.from({ length: days }, (_, i) => { const d = new Date(today); d.setUTCDate(d.getUTCDate() - (days - 1 - i)); return d.toISOString().slice(0, 10); });
  const map = Object.fromEntries((series || []).map((r) => [r.day, r]));
  const clicks = dayKeys.map((k) => map[k]?.click || 0);
  const subs = dayKeys.map((k) => map[k]?.submitted || 0);
  const W = 900, H = 90, pad = 4;
  const max = Math.max(1, ...clicks, ...subs);
  const stepX = (W - pad * 2) / (days - 1);
  const path = (vals) => vals.map((v, i) => `${i ? "L" : "M"}${(pad + i * stepX).toFixed(1)},${(H - pad - (v / max) * (H - pad * 2)).toFixed(1)}`).join(" ");
  const fmt = (k) => new Date(`${k}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  return (
    <div className="mt-4" data-testid="rfq-banner-trend">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-wider text-[#7D8C85]">Last {days} days · daily</div>
        <div className="flex items-center gap-3 text-[10px] text-[#7D8C85]">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 bg-[#264839] inline-block" /> Clicks</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 bg-[#B8860B] inline-block" /> RFQs submitted</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto max-h-[110px] overflow-visible" role="img" aria-label={`Banner clicks and submitted RFQs over the last ${days} days`} data-max={max} data-clicks={clicks.reduce((a, b) => a + b, 0)} data-submitted={subs.reduce((a, b) => a + b, 0)}>
        <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke="#E8ECE9" strokeWidth="1" />
        <path d={path(clicks)} fill="none" stroke="#264839" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
        <path d={path(subs)} fill="none" stroke="#B8860B" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" strokeDasharray={subs.some(Boolean) ? undefined : "3 3"} />
        {dayKeys.map((k, i) => (
          <g key={k}>
            <circle cx={pad + i * stepX} cy={H - pad - (clicks[i] / max) * (H - pad * 2)} r={clicks[i] ? 2.5 : 0} fill="#264839"><title>{fmt(k)} · {clicks[i]} clicks</title></circle>
            <circle cx={pad + i * stepX} cy={H - pad - (subs[i] / max) * (H - pad * 2)} r={subs[i] ? 2.5 : 0} fill="#B8860B"><title>{fmt(k)} · {subs[i]} RFQs</title></circle>
          </g>
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-[#9AA6A0] mt-0.5"><span>{fmt(dayKeys[0])}</span><span>{fmt(dayKeys[dayKeys.length - 1])}</span></div>
    </div>
  );
}

// Homepage RFQ banner funnel: clicks → started (first field typed) → RFQs submitted.
export default function RfqBannerStats() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  useEffect(() => { api.get(`/admin/rfq-banner/stats?days=${days}`).then(({ data }) => setData(data)).catch(() => {}); }, [days]);
  useEffect(() => { api.get("/admin/rfq-banner/stats?days=14").then(({ data }) => setTrend(data.series || [])).catch(() => setTrend([])); }, []);
  if (!data) return null;
  const t = data.totals;
  const cells = [
    { key: "click", label: "Banner clicks", value: t.click, hint: "Request quotes pressed" },
    { key: "started", label: "RFQs started", value: t.started, hint: `${t.click_to_started}% of clicks` },
    { key: "submitted", label: "RFQs submitted", value: t.submitted, hint: `${t.click_to_submitted}% of clicks`, accent: true },
  ];
  return (
    <div className="bg-white border border-[#E8ECE9] p-5 mb-6" data-testid="rfq-banner-stats">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="tracking-overline text-[10px] text-[#264839] inline-flex items-center gap-1.5"><Megaphone className="w-3 h-3" /> Homepage RFQ banner</div>
          <div className="text-xs text-[#7D8C85] mt-0.5">Click → form started → RFQ sent. All-time: {data.all_time.click} clicks · {data.all_time.submitted} RFQs ({data.all_time.click_to_submitted}%).</div>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} type="button" onClick={() => setDays(d)} className={`text-[10px] uppercase tracking-wider px-2.5 py-1 border transition ${days === d ? "bg-[#264839] text-white border-[#264839]" : "border-[#C4CFC9] text-[#4A5550] hover:border-[#264839]"}`} data-testid={`rfq-banner-range-${d}`}>{d}d</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {cells.map((c) => (
          <div key={c.key} className="bg-[#FAFAF7] border border-[#E8ECE9] px-4 py-3" data-testid={`rfq-banner-${c.key}`}>
            <div className={`font-serif text-2xl ${c.accent ? "text-[#B8860B]" : "text-[#11211A]"}`}>{c.value}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#7D8C85]">{c.label}</div>
            <div className="text-[10px] text-[#9AA6A0]">{c.hint}</div>
          </div>
        ))}
      </div>
      <TrendChart series={trend || []} days={14} />
      {data.by_category.length > 0 && (
        <table className="w-full text-xs mt-4" data-testid="rfq-banner-by-category">
          <thead className="text-left text-[10px] uppercase tracking-wider text-[#7D8C85] border-b border-[#E8ECE9]">
            <tr><th className="py-1.5">Category shown</th><th className="text-right">Clicks</th><th className="text-right">Started</th><th className="text-right">Submitted</th><th className="text-right">Conversion</th></tr>
          </thead>
          <tbody>
            {data.by_category.map((r) => (
              <tr key={r.category} className="border-b border-[#F0EFEA]">
                <td className="py-1.5 text-[#11211A]">{r.category}</td>
                <td className="py-1.5 text-right tabular-nums">{r.click}</td>
                <td className="py-1.5 text-right tabular-nums">{r.started}</td>
                <td className="py-1.5 text-right tabular-nums text-[#B8860B]">{r.submitted}</td>
                <td className="py-1.5 text-right tabular-nums">{r.click ? `${r.click_to_submitted}%` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
